import { useEffect, useState } from 'react'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import DialogCustom from '../../../dialog/Dialog'
import ButtonStartIcon from '../../../button/ButtonStartIcon'
import InputDateUTC from '../../../FormControl/input/InputDateUTC'
import AutocompleteField from '../../../FormControl/input/AutocompleteField'
import SelectField from '../../../FormControl/input/SelectField'
import useFetchData from '../../../../hooks/useFetchData'
import { useDispatch, useSelector } from 'react-redux'
import { updateFilter, showFilter } from '../../../../store/actions/filter.actions'
import { useLocation, useNavigate } from 'react-router-dom'
import isEmpty from 'lodash/isEmpty'
import { updateSkip } from '../../../../store/actions/datatable.actions'
import { capitalizarPrimeraLetra } from '../../../../utils'
import { addDays, differenceInDays } from 'date-fns'

const MAX_DATE_RANGE_DAYS = 30

const FilterDialog = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const date = useSelector((state) => state?.date?.date || new Date())
  const filter = useSelector((state) => state?.filter.audit?.filter || {})
  const open = useSelector((state) => state?.filter.audit?.open || false)
  const dispatch = useDispatch()
  const { isLoading: isLoadingModule, data: dataModule } = useFetchData('audit/modules')
  const { isLoading: isLoadingUsers, data: dataUsers } = useFetchData('audit/users')

  const [form, setInputForm] = useState(filter)
  const [error, setInputError] = useState({})

  useEffect(() => {
    if (!isEmpty(filter)) {
      setInputForm(filter)
    }
  }, [filter])

  const onClose = () => {
    dispatch(showFilter('audit', false))
  }

  const onChange = (name, value) => {
    if (name === 'startDate') {
      const newForm = { ...form, startDate: value }
      if (form.endDate && differenceInDays(new Date(form.endDate), new Date(value)) > MAX_DATE_RANGE_DAYS) {
        newForm.endDate = null
      }
      setInputForm(newForm)
      setInputError({ ...error, startDate: null, endDate: null })
    } else if (name === 'action') {
      setInputForm({ ...form, [name]: value, actionId: value?.value })
      setInputError({ ...error, [name]: null, actionId: null })
    } else if (name === 'user') {
      if (value) {
        setInputForm({ ...form, [name]: value, userId: value?.value })
        setInputError({ ...error, [name]: null, userId: null })
      } else {
        setInputForm({ ...form, [name]: null, userId: null })
        setInputError({ ...error, [name]: null, userId: null })
      }
    } else if (name === 'roleId') {
      const role = dataUsers?.roles?.find((item) => item._id === value)
      setInputForm({ ...form, [name]: value, role })
      setInputError({ ...error, [name]: null, roleId: null })
    } else if (name === 'module') {
      setInputForm({ ...form, [name]: value, action: '', actionId: '', moduleId: value?.value })
      setInputError({ ...error, [name]: null, action: null })
    } else {
      setInputForm({ ...form, [name]: value })
      setInputError({ ...error, [name]: null })
    }
  }

  const onApply = () => {
    if (form.startDate && form.endDate) {
      const diff = differenceInDays(new Date(form.endDate), new Date(form.startDate))
      if (diff > MAX_DATE_RANGE_DAYS) {
        setInputError({ endDate: `Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days` })
        return
      }
    }
    onClose()
    dispatch(updateFilter('audit', form))
    dispatch(updateSkip('audit', 0))
    navigate(pathname, { state: form })
  }

  const Actions = (
    <>
      <ButtonStartIcon onClick={onClose} color='primary' variant='outlined' startIcon={['fal', 'times']}>
        Cancel
      </ButtonStartIcon>
      <ButtonStartIcon onClick={onApply} color='primary' variant='contained' startIcon={['fal', 'check']}>
        Apply
      </ButtonStartIcon>
    </>
  )

  return (
    <DialogCustom id='audit-log-filter' show={open} title='Filter' onClose={onClose} actions={Actions}>
      <Box className='flex flex-col gap-4 py-4'>
        <div className='grid grid-cols-3 items-start gap-4'>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            start date
          </Typography>
          <div className='col-span-2'>
            <InputDateUTC
              id='form-filter-start-date'
              name='startDate'
              value={form.startDate}
              error={error.startDate}
              helperText={error.startDate}
              maxDate={date}
              onChange={(value) => onChange('startDate', value)}
            />
          </div>
        </div>
        <div className='grid grid-cols-3 items-start gap-4'>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            end date
          </Typography>
          <div className='col-span-2'>
            <InputDateUTC
              id='form-filter-end-date'
              name='endDate'
              value={form.endDate}
              error={error.endDate}
              helperText={
                error.endDate ||
                (form.startDate ? `Max ${MAX_DATE_RANGE_DAYS} days from start date` : undefined)
              }
              minDate={form.startDate}
              maxDate={
                form.startDate
                  ? addDays(new Date(form.startDate), MAX_DATE_RANGE_DAYS) < new Date(date)
                    ? addDays(new Date(form.startDate), MAX_DATE_RANGE_DAYS)
                    : date
                  : date
              }
              isStartOfTheDay={false}
              onChange={(value) => onChange('endDate', value)}
            />
          </div>
        </div>
        <div className='grid grid-cols-3 items-start gap-4'>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            module
          </Typography>
          <div className='col-span-2'>
            <AutocompleteField
              id='form-filter-module'
              name='module'
              error={error.module}
              helperText={error.module}
              value={form.module}
              className='capitalize'
              placeholder='Choose a module'
              loading={isLoadingModule}
              onChange={(value) => onChange('module', value)}
              options={
                dataModule?.map((item) => ({
                  value: item.module,
                  label: capitalizarPrimeraLetra(item.module)
                })) || []
              }
            />
          </div>
        </div>
        <div className='grid grid-cols-3 items-start gap-4'>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            action
          </Typography>
          <div className='col-span-2'>
            <AutocompleteField
              id='form-filter-action'
              name='action'
              className='capitalize'
              error={error.action}
              helperText={error.action}
              value={form.action}
              placeholder='Choose an action'
              loading={isLoadingModule}
              onChange={(value) => onChange('action', value)}
              disabled={isLoadingModule || !form.module || form.module === ''}
              options={
                dataModule
                  ?.find((item) => item.module === form.moduleId)
                  ?.actions?.map((item) => ({
                    value: item,
                    label: capitalizarPrimeraLetra(item),
                    className: 'capitalize'
                  })) || []
              }
            />
          </div>
        </div>
        <div className='grid grid-cols-3 items-start gap-4'>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            User
          </Typography>
          <div className='col-span-2'>
            <AutocompleteField
              id='form-filter-users'
              name='user'
              placeholder='Choose an user'
              options={
                dataUsers?.users?.map((item) => ({
                  value: item._id,
                  label: `${item.code} ${item.code !== item.fullName ? `- ${item.fullName}` : ''}`
                })) || []
              }
              onChange={(value) => onChange('user', value)}
              loading={isLoadingUsers}
              value={form.user}
            />
          </div>
        </div>
        <div className='grid grid-cols-3 items-start gap-4'>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Roles
          </Typography>
          <div className='col-span-2'>
            <SelectField
              id='form-filter-role'
              name='roleId'
              className='capitalize'
              error={error.roleId}
              helperText={error.roleId}
              value={form.roleId}
              onChange={(e) => onChange(e.target.name, e.target.value)}
              options={dataUsers?.roles?.map((item) => ({ value: item._id, label: item.name })) || []}
              loading={isLoadingUsers}
              displayEmpty={true}
              placeholder='Choose a role'
            />
          </div>
        </div>
      </Box>
    </DialogCustom>
  )
}

export default FilterDialog
